/* ============================================================================
   SAKURA COLLECTORS CLUB — micro-interactions
   ============================================================================

   Chargé en `defer`, sans dépendance, sans bibliothèque, sans import.
   Le site reste entièrement utilisable si ce fichier ne se charge jamais :
   les accordéons sont des <details> natifs, les formulaires d'ajout au panier
   sont des <form> natifs, et oripaya-animations.css révèle le contenu de
   lui-même au bout de trois secondes si ce script ne s'annonce pas.

   Cinq modules, tous indépendants les uns des autres :
     01. Apparitions au défilement — UN observateur partagé, désobservation
         immédiate après déclenchement.
     02. Défilement partagé — UN écouteur passif cadencé en rAF, exposé aux
         autres agents pour qu'aucun second écouteur ne soit ajouté.
     03. Accordéons — amélioration progressive de <details> : la fermeture
         devient animée, ce que le natif ne sait pas faire.
     04. Drawers — ouverture, piège de focus, Échap, restitution du focus,
         verrou de défilement sans saut de mise en page.
     05. Confirmation d'ajout au panier — brève, non bloquante, jamais
         redondante avec le tiroir panier du thème.

   API publique : window.Oripaya
     .apparitions.scanner(racine)      observe les nouveaux .sk-apparait
     .apparitions.tout()               révèle tout immédiatement
     .defilement.abonner(fn)           renvoie la fonction de désabonnement
     .defilement.etat()                { y, sens, seuil }
     .drawer.ouvrir(cible[, source])   cible = élément ou sélecteur
     .drawer.fermer([cible])           sans argument : le drawer du dessus
     .confirmation.montrer(opts)       { etat, intitule, titre, lien }

   Réglages facultatifs, à définir AVANT ce script par une section :
     window.ORIPAYA_TEXTES = { intitule, intitule_erreur, voir, fermer, delai }
     window.ORIPAYA_CONFIRMATION = true | false   (défaut : détection auto)
     <html data-sk-seuil="80">                    seuil de l'en-tête, en px
   ============================================================================ */

(function () {
  'use strict';

  var API = (window.Oripaya = window.Oripaya || {});
  if (API.pret) return; /* le fichier peut être inclus par plusieurs sections */

  var racine = document.documentElement;
  var REDUIT = window.matchMedia('(prefers-reduced-motion: reduce)');

  function tableau(liste) {
    return Array.prototype.slice.call(liste);
  }

  /* ==========================================================================
     01. LES APPARITIONS AU DÉFILEMENT
     ==========================================================================

     ATTENTION LCP — un élément à opacity: 0 n'est pas peint, donc ne compte
     pas comme Largest Contentful Paint. Ne JAMAIS poser .sk-apparait sur
     l'image de hero ni sur le titre principal d'une page : cela déplacerait
     mécaniquement le LCP de plusieurs centaines de millisecondes. La classe
     est faite pour ce qui se trouve sous la ligne de flottaison.
     ---------------------------------------------------------------------- */

  var observateur = null;

  function revelerTout() {
    tableau(document.querySelectorAll('.sk-apparait:not(.sk-vu)')).forEach(function (n) {
      n.classList.add('sk-vu');
    });
  }

  function surCroisement(entrees, obs) {
    for (var i = 0; i < entrees.length; i++) {
      if (!entrees[i].isIntersecting) continue;
      entrees[i].target.classList.add('sk-vu');
      /* Désobservation immédiate : l'apparition ne joue qu'une fois, et sur
         une collection de 242 produits on ne garde pas 242 cibles vivantes. */
      obs.unobserve(entrees[i].target);
    }
  }

  function scanner(depuis) {
    if (REDUIT.matches || !('IntersectionObserver' in window)) return revelerTout();
    if (!observateur) {
      observateur = new IntersectionObserver(surCroisement, {
        /* La marge négative en bas fait déclencher l'apparition une fois
           l'élément franchement entré, pas au premier pixel : sinon tout se
           révèle pendant que l'on défile vite, et l'effet ne se voit pas. */
        rootMargin: '0px 0px -10% 0px',
        threshold: 0.01
      });
    }
    var noeuds = (depuis || document).querySelectorAll('.sk-apparait:not(.sk-vu)');
    for (var i = 0; i < noeuds.length; i++) observateur.observe(noeuds[i]);
  }

  API.apparitions = { scanner: scanner, tout: revelerTout };

  scanner(document);

  /* Le contenu injecté après coup doit être pris en charge : rechargement
     d'une section dans l'éditeur, pagination, filtres de collection. */
  document.addEventListener('shopify:section:load', function (e) {
    scanner(e.target);
  });
  document.addEventListener('cart-update', function () {
    scanner(document);
  });
  /* Le thème rend certaines listes après la première frame (filtres, tri) :
     une passe de rattrapage au chargement complet, et c'est tout. */
  window.addEventListener('load', function () {
    scanner(document);
  });

  if (REDUIT.addEventListener) {
    REDUIT.addEventListener('change', function () {
      if (REDUIT.matches) revelerTout();
    });
  }

  /* ==========================================================================
     02. LE DÉFILEMENT PARTAGÉ
     ==========================================================================

     Un seul écouteur passif pour tout le thème. Le thème embarque déjà Lenis :
     multiplier les écouteurs de défilement est la première cause de saccade
     sur mobile. Les autres agents s'abonnent ici plutôt que d'en ajouter un.
     ---------------------------------------------------------------------- */

  var abonnes = [];
  var seuil = parseInt(racine.getAttribute('data-sk-seuil'), 10);
  if (isNaN(seuil)) seuil = 80;

  var dernierY = window.pageYOffset || racine.scrollTop || 0;
  var sens = 'haut';
  var enAttente = false;

  function lireDefilement() {
    enAttente = false;
    var y = window.pageYOffset || racine.scrollTop || 0;
    if (y < 0) y = 0;
    var delta = y - dernierY;

    /* Hystérésis de 4 px : sans elle, l'élasticité de fin de page sur iOS
       ferait clignoter l'en-tête entre « monte » et « descend ». */
    if (Math.abs(delta) >= 4) {
      sens = delta > 0 ? 'bas' : 'haut';
      dernierY = y;
    }

    var passe = y > seuil;
    racine.classList.toggle('sk-defile', passe);
    racine.classList.toggle('sk-defile--bas', passe && sens === 'bas');
    racine.classList.toggle('sk-defile--haut', passe && sens === 'haut');

    for (var i = 0; i < abonnes.length; i++) {
      try {
        abonnes[i]({ y: y, delta: delta, sens: sens, seuil: seuil });
      } catch (err) {
        /* Un abonné fautif ne doit pas emporter le défilement de la page. */
      }
    }
  }

  function surDefilement() {
    if (enAttente) return;
    enAttente = true;
    requestAnimationFrame(lireDefilement);
  }

  window.addEventListener('scroll', surDefilement, { passive: true });
  window.addEventListener('resize', surDefilement, { passive: true });
  lireDefilement();

  API.defilement = {
    abonner: function (fn) {
      if (typeof fn !== 'function') return function () {};
      abonnes.push(fn);
      fn({ y: dernierY, delta: 0, sens: sens, seuil: seuil });
      return function () {
        var i = abonnes.indexOf(fn);
        if (i > -1) abonnes.splice(i, 1);
      };
    },
    etat: function () {
      return { y: dernierY, sens: sens, seuil: seuil };
    }
  };

  /* ==========================================================================
     03. LES ACCORDÉONS
     ==========================================================================

     <details> natif : l'ouverture s'anime déjà en CSS (grid-template-rows
     0fr → 1fr, oripaya-composants.css). La FERMETURE, elle, est instantanée
     par construction : le navigateur retire le contenu du rendu à la seconde
     où l'attribut `open` disparaît. On retarde donc ce retrait le temps de la
     transition, et pas une milliseconde de plus.

     Sans ce script, tout continue de fonctionner : l'accordéon s'ouvre et se
     ferme, simplement la fermeture est sèche.
     ---------------------------------------------------------------------- */

  function fermerEnDouceur(details) {
    if (details.__skFermeture) return;
    details.__skFermeture = true;
    details.classList.add('sk-ferme-en-cours');

    var corps = details.querySelector('.sk-accordeon__corps');
    var minuteur = 0;

    function terminer() {
      clearTimeout(minuteur);
      if (corps) corps.removeEventListener('transitionend', surFin);
      details.classList.remove('sk-ferme-en-cours');
      details.open = false;
      details.__skFermeture = false;
    }

    function surFin(ev) {
      /* On n'écoute que la transition du corps lui-même : les transitions des
         enfants (chevron, liens survolés) remontent aussi jusqu'ici. */
      if (ev.target === corps) terminer();
    }

    if (corps) corps.addEventListener('transitionend', surFin);
    /* Filet : si la transition n'a jamais lieu (corps absent, display:none
       hérité, navigateur qui n'anime pas les unités fr), on ferme quand même. */
    minuteur = setTimeout(terminer, 600);
  }

  document.addEventListener('click', function (e) {
    var cible = e.target;
    if (!cible || !cible.closest) return;

    var tete = cible.closest('summary.sk-accordeon__tete');
    if (!tete) return;

    var details = tete.parentElement;
    if (!details || details.tagName !== 'DETAILS') return;
    if (!details.classList.contains('sk-accordeon')) return;

    /* Mouvement réduit : on laisse le comportement natif, instantané. */
    if (REDUIT.matches) return;
    /* L'ouverture n'a besoin de personne. */
    if (!details.open) return;

    e.preventDefault();
    fermerEnDouceur(details);
  });

  /* ==========================================================================
     04. LES DRAWERS
     ==========================================================================

     Implémente exactement le contrat documenté en tête de la section 04 de
     oripaya-composants.css.

     Déclencheurs : [data-sk-ouvre="#identifiant"] (ou un sélecteur CSS).
     Fermetures   : [data-sk-fermer], clic sur .sk-drawer__voile, touche Échap.

     Ce module ne touche QUE les éléments .sk-drawer. Les tiroirs natifs du
     thème Nexus (panier, recherche, menu mobile) gardent leur propre code :
     deux gestionnaires sur le même panneau se battraient pour le focus.
     ---------------------------------------------------------------------- */

  var FOCUSABLES =
    'a[href],area[href],button:not([disabled]),input:not([disabled]):not([type="hidden"]),' +
    'select:not([disabled]),textarea:not([disabled]),summary,iframe,object,embed,' +
    '[tabindex]:not([tabindex="-1"]),[contenteditable="true"]';

  var pile = [];
  var supporteInert = 'inert' in HTMLElement.prototype;

  function estVisible(el) {
    return !!(el.offsetWidth || el.offsetHeight || el.getClientRects().length);
  }

  function focusables(panneau) {
    return tableau(panneau.querySelectorAll(FOCUSABLES)).filter(estVisible);
  }

  /* Verrou de défilement. La gouttière est réservée en permanence par
     oripaya-animations.css (scrollbar-gutter: stable) ; la mesure ci-dessous
     n'est utile qu'aux navigateurs qui ne connaissent pas cette propriété. */
  function verrouiller() {
    var compensation = window.innerWidth - racine.clientWidth;
    if (compensation > 0) racine.style.setProperty('--sk-compensation-barre', compensation + 'px');
    racine.classList.add('sk-scroll-bloque');
  }

  function deverrouiller() {
    racine.classList.remove('sk-scroll-bloque');
    racine.style.removeProperty('--sk-compensation-barre');
  }

  /* Rend inerte tout ce qui n'est pas le drawer : on remonte la chaîne des
     ancêtres et on neutralise les frères et sœurs à chaque étage. C'est la
     seule méthode fiable quand le drawer est imbriqué dans une section. */
  function inerter(drawer, actif) {
    if (actif) {
      var touches = [];
      var noeud = drawer;
      var parent = drawer.parentElement;
      while (parent && parent !== racine) {
        for (var i = 0; i < parent.children.length; i++) {
          var el = parent.children[i];
          if (el === noeud) continue;
          var nom = el.tagName;
          if (nom === 'SCRIPT' || nom === 'LINK' || nom === 'STYLE' || nom === 'TEMPLATE') continue;
          if (el.hasAttribute('inert')) continue; /* déjà neutralisé par un autre drawer */
          el.setAttribute('inert', '');
          if (!supporteInert) el.setAttribute('data-sk-inert-secours', '');
          touches.push(el);
        }
        noeud = parent;
        parent = parent.parentElement;
      }
      drawer.__skInertes = touches;
      return;
    }

    var liste = drawer.__skInertes || [];
    for (var j = 0; j < liste.length; j++) {
      liste[j].removeAttribute('inert');
      liste[j].removeAttribute('data-sk-inert-secours');
    }
    drawer.__skInertes = null;
  }

  function resoudre(cible) {
    if (!cible) return null;
    if (typeof cible === 'string') {
      var sel = cible.charAt(0) === '#' || /[.\[\s>]/.test(cible) ? cible : '#' + cible;
      return document.querySelector(sel);
    }
    return cible.nodeType === 1 ? cible : null;
  }

  function ouvrir(cible, source) {
    var drawer = resoudre(cible);
    if (!drawer || !drawer.classList.contains('sk-drawer')) return null;
    if (pile.indexOf(drawer) > -1) return drawer;

    drawer.__skSource = source || document.activeElement;
    pile.push(drawer);
    if (pile.length === 1) verrouiller();

    inerter(drawer, true);
    drawer.setAttribute('aria-hidden', 'false');
    drawer.classList.add('sk-ouvert');

    var panneau = drawer.querySelector('.sk-drawer__panneau') || drawer;
    if (!panneau.hasAttribute('tabindex')) panneau.setAttribute('tabindex', '-1');

    /* Le focus est donné à la frame suivante : posé immédiatement, il ferait
       défiler la page pendant que le panneau glisse encore. */
    requestAnimationFrame(function () {
      var premier = drawer.querySelector('[data-sk-focus]') || panneau;
      try {
        premier.focus({ preventScroll: true });
      } catch (err) {
        premier.focus();
      }
    });

    drawer.dispatchEvent(new CustomEvent('sk:drawer:ouvert', { bubbles: true }));
    return drawer;
  }

  function fermer(cible) {
    var drawer = cible ? resoudre(cible) : pile[pile.length - 1];
    var index = pile.indexOf(drawer);
    if (index < 0) return;

    pile.splice(index, 1);
    drawer.setAttribute('aria-hidden', 'true');
    drawer.classList.remove('sk-ouvert');
    inerter(drawer, false);
    if (!pile.length) deverrouiller();

    var source = drawer.__skSource;
    drawer.__skSource = null;
    /* Restitution du focus : sans elle, la tabulation repart du haut du
       document, et l'on perd sa place dans une grille de 242 produits. */
    if (source && document.contains(source)) {
      try {
        source.focus({ preventScroll: true });
      } catch (err) {
        source.focus();
      }
    }

    drawer.dispatchEvent(new CustomEvent('sk:drawer:ferme', { bubbles: true }));
  }

  document.addEventListener('click', function (e) {
    var cible = e.target;
    if (!cible || !cible.closest) return;

    var declencheur = cible.closest('[data-sk-ouvre]');
    if (declencheur) {
      var vise = resoudre(declencheur.getAttribute('data-sk-ouvre'));
      if (vise && vise.classList.contains('sk-drawer')) {
        e.preventDefault();
        ouvrir(vise, declencheur);
        return;
      }
    }

    var fermeture = cible.closest('[data-sk-fermer]');
    if (fermeture && fermeture.closest('.sk-drawer')) {
      e.preventDefault();
      fermer(fermeture.closest('.sk-drawer'));
      return;
    }

    /* Le voile ferme même sans attribut : c'est ce que tout le monde attend. */
    if (cible.classList && cible.classList.contains('sk-drawer__voile')) {
      fermer(cible.closest('.sk-drawer'));
    }
  });

  document.addEventListener('keydown', function (e) {
    if (!pile.length) return;
    var drawer = pile[pile.length - 1];

    if (e.key === 'Escape' || e.key === 'Esc') {
      fermer(drawer);
      return;
    }
    if (e.key !== 'Tab') return;

    var panneau = drawer.querySelector('.sk-drawer__panneau') || drawer;
    var liste = focusables(panneau);
    if (!liste.length) {
      e.preventDefault();
      panneau.focus();
      return;
    }

    var premier = liste[0];
    var dernier = liste[liste.length - 1];
    var actif = document.activeElement;

    if (e.shiftKey && (actif === premier || actif === panneau || !panneau.contains(actif))) {
      e.preventDefault();
      dernier.focus();
    } else if (!e.shiftKey && actif === dernier) {
      e.preventDefault();
      premier.focus();
    }
  });

  API.drawer = { ouvrir: ouvrir, fermer: fermer, ouverts: function () { return pile.slice(); } };

  /* ==========================================================================
     05. LA CONFIRMATION D'AJOUT AU PANIER
     ==========================================================================

     Le thème Nexus dispatche `cart-update` sur `document` après chaque ajout
     (assets/critical.js, classe ProductForm) avec :
        detail.button   le bouton d'origine — présent uniquement sur un AJOUT
        detail.message  le message d'erreur — présent uniquement sur un échec

     Quand la boutique est réglée sur « tiroir panier », le thème ouvre déjà
     son tiroir : une confirmation flottante par-dessus serait un doublon.
     Ce module ne s'active donc QUE si aucun tiroir panier n'existe dans la
     page — ou si le marchand force window.ORIPAYA_CONFIRMATION = true.

     Non bloquante : ne prend jamais le focus, ne pose aucun voile, ne
     verrouille pas le défilement. role="status" + aria-live="polite" :
     annoncée aux lecteurs d'écran sans interrompre la lecture en cours.
     ---------------------------------------------------------------------- */

  var TEXTES = {
    intitule: 'Ajouté au panier',
    intitule_erreur: 'Ajout impossible',
    voir: 'Voir le panier',
    fermer: 'Fermer',
    delai: 5000
  };
  var perso = window.ORIPAYA_TEXTES || {};
  for (var cle in perso) {
    if (Object.prototype.hasOwnProperty.call(perso, cle)) TEXTES[cle] = perso[cle];
  }

  var COCHE = '<svg viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M4 10.5 8 14.5 16 5.5"/></svg>';
  var CROIX = '<svg viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" aria-hidden="true"><path d="M5 5l10 10M15 5L5 15"/></svg>';

  var boite = null;
  var minuteurConf = 0;

  function confirmationActive() {
    if (window.ORIPAYA_CONFIRMATION === true) return true;
    if (window.ORIPAYA_CONFIRMATION === false) return false;
    /* Détection : le thème n'insère <cart-drawer> que si le réglage boutique
       est « tiroir ». S'il est là, on se tait. */
    return !document.querySelector('cart-drawer, .cart_drawer_wrapper');
  }

  function urlPanier() {
    var base = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return base + 'cart';
  }

  function texteBrut(html) {
    var d = document.createElement('div');
    d.innerHTML = html == null ? '' : String(html);
    return (d.textContent || '').trim();
  }

  function titreDepuis(bouton) {
    if (!bouton || !bouton.closest) return '';
    if (bouton.getAttribute && bouton.getAttribute('data-sk-titre')) {
      return bouton.getAttribute('data-sk-titre');
    }
    var carte = bouton.closest('[data-oripaya-carte]');
    var titre = carte && carte.querySelector('.oripaya-carte__titre');
    if (!titre) {
      var fiche = bouton.closest('product-info, .main-product-section');
      titre = fiche && fiche.querySelector('h1');
    }
    return titre ? (titre.textContent || '').trim() : '';
  }

  function construire() {
    if (boite) return boite;
    boite = document.createElement('div');
    boite.className = 'sk-confirmation';
    boite.setAttribute('role', 'status');
    boite.setAttribute('aria-live', 'polite');
    document.body.appendChild(boite);

    /* Pause du décompte tant que le pointeur ou le focus est dessus : on ne
       retire pas une information sous les yeux de qui est en train de la lire
       (WCAG 2.2.1). */
    boite.addEventListener('mouseenter', pauser);
    boite.addEventListener('mouseleave', reprendre);
    boite.addEventListener('focusin', pauser);
    boite.addEventListener('focusout', reprendre);
    return boite;
  }

  function pauser() {
    if (!boite) return;
    boite.setAttribute('data-pause', 'true');
    clearTimeout(minuteurConf);
  }

  function reprendre() {
    if (!boite || !boite.firstChild) return;
    boite.removeAttribute('data-pause');
    programmer(2000);
  }

  function programmer(delai) {
    clearTimeout(minuteurConf);
    minuteurConf = setTimeout(masquer, delai);
  }

  function masquer() {
    if (!boite || !boite.firstChild) return;
    clearTimeout(minuteurConf);
    boite.setAttribute('data-sortie', 'true');
    /* On attend la fin de l'animation de sortie, sans dépendre d'elle : le
       minuteur vide la boîte même si l'animation est désactivée.
       Si une nouvelle confirmation est arrivée entre-temps, elle a retiré
       data-sortie — on ne doit surtout pas effacer son contenu. */
    setTimeout(function () {
      if (!boite || boite.getAttribute('data-sortie') !== 'true') return;
      boite.removeAttribute('data-sortie');
      boite.removeAttribute('data-etat');
      boite.textContent = '';
    }, 260);
  }

  function montrer(options) {
    var opt = options || {};
    var b = construire();
    var erreur = opt.etat === 'erreur';
    /* Sans animation, la jauge n'existe pas : on laisse davantage de temps
       pour lire, puisque plus rien n'indique le décompte. */
    var delai = opt.delai || TEXTES.delai;
    if (REDUIT.matches) delai = Math.round(delai * 1.6);

    clearTimeout(minuteurConf);
    b.removeAttribute('data-sortie');
    b.removeAttribute('data-pause');
    b.setAttribute('data-etat', erreur ? 'erreur' : 'succes');
    b.style.setProperty('--sk-confirmation-delai', delai + 'ms');
    b.textContent = '';

    var panneau = document.createElement('div');
    panneau.className = 'sk-confirmation__panneau';

    var pastille = document.createElement('span');
    pastille.className = 'sk-confirmation__pastille';
    pastille.setAttribute('aria-hidden', 'true');
    pastille.innerHTML = erreur ? CROIX : COCHE;
    panneau.appendChild(pastille);

    var texte = document.createElement('div');
    texte.className = 'sk-confirmation__texte';

    var intitule = document.createElement('p');
    intitule.className = 'sk-confirmation__intitule';
    intitule.textContent = opt.intitule || (erreur ? TEXTES.intitule_erreur : TEXTES.intitule);
    texte.appendChild(intitule);

    if (opt.titre) {
      var titre = document.createElement('p');
      titre.className = 'sk-confirmation__titre';
      /* textContent, jamais innerHTML : le titre vient du DOM produit. */
      titre.textContent = opt.titre;
      texte.appendChild(titre);
    }
    panneau.appendChild(texte);

    var actions = document.createElement('div');
    actions.className = 'sk-confirmation__actions';

    if (!erreur) {
      var lien = document.createElement('a');
      lien.className = 'sk-btn sk-btn--secondaire';
      lien.href = opt.lien || urlPanier();
      lien.textContent = opt.texteLien || TEXTES.voir;
      actions.appendChild(lien);
    }

    if (actions.childNodes.length) panneau.appendChild(actions);

    /* La fermeture est posée directement sur le panneau : elle est calée dans
       l'angle par le CSS, hors du flux de la grille. */
    var fermeture = document.createElement('button');
    fermeture.type = 'button';
    fermeture.className = 'sk-btn sk-btn--icone sk-confirmation__fermer';
    fermeture.setAttribute('aria-label', TEXTES.fermer);
    fermeture.innerHTML = CROIX;
    fermeture.addEventListener('click', masquer);
    panneau.appendChild(fermeture);

    var jauge = document.createElement('span');
    jauge.className = 'sk-confirmation__jauge';
    jauge.setAttribute('aria-hidden', 'true');
    panneau.appendChild(jauge);

    b.appendChild(panneau);
    programmer(delai);
  }

  API.confirmation = { montrer: montrer, masquer: masquer };

  document.addEventListener('keydown', function (e) {
    if ((e.key === 'Escape' || e.key === 'Esc') && boite && boite.firstChild && !pile.length) masquer();
  });

  document.addEventListener('cart-update', function (e) {
    var detail = e.detail || {};

    /* Le compteur du panier bat une fois. Une seule pulsation, jamais de
       boucle : c'est un accusé de réception, pas une alarme. */
    tableau(document.querySelectorAll('[data-sk-compteur], .cart-count-text')).forEach(function (n) {
      n.classList.remove('sk-battement');
      /* Reflow forcé pour que le retrait/ajout de classe rejoue l'animation. */
      void n.offsetWidth;
      n.classList.add('sk-battement');
      n.addEventListener(
        'animationend',
        function () {
          n.classList.remove('sk-battement');
        },
        { once: true }
      );
    });

    if (!confirmationActive()) return;

    if (detail.message) {
      montrer({ etat: 'erreur', titre: texteBrut(detail.message) });
      return;
    }
    /* Pas de bouton : c'est une mise à jour de quantité ou de code promo dans
       le panier, pas un ajout. Rien à confirmer. */
    if (!detail.button) return;

    montrer({ titre: titreDepuis(detail.button) });
  });

  /* ==========================================================================
     SIGNATURE
     ==========================================================================
     Pose la classe qui désarme le filet de sécurité de oripaya-animations.css.
     Placée en dernier : si l'un des modules ci-dessus jetait une exception,
     la classe ne serait pas posée et le contenu se révélerait tout seul.
     ---------------------------------------------------------------------- */

  racine.classList.add('sk-interactions');
  API.pret = true;
})();
